import {
  createContext, useCallback, useContext, useEffect, useMemo, useRef, useState,
  type ReactNode,
} from 'react';

/**
 * "Saved." where the eye already is.
 *
 * Every save used to answer with a banner at the top of the page, which is the
 * one place a person who has just scrolled to the bottom of a long settings
 * form is not looking: they pressed Save, nothing appeared to happen, and the
 * only way to find out whether it had was to scroll back up. So the answer
 * comes to them instead — pinned to the bottom-right corner of the WINDOW,
 * above whatever is on screen, and gone again after ten seconds.
 *
 * Deliberately not a replacement for every message. A toast is right for the
 * outcome of an action you just took; it is wrong for a standing fact about
 * the screen (a plan limit, a failing provider, "no card on file"), which has
 * to stay put and be readable long after ten seconds have passed. Errors that
 * belong to a FIELD stay by that field for the same reason.
 */

export type ToastKind = 'ok' | 'error' | 'info';

export interface ToastOptions {
  kind?: ToastKind;
  /** Milliseconds on screen. 0 keeps it until it is dismissed by hand. */
  duration?: number;
}

/** The brief: ten seconds. Long enough to notice mid-scroll, short enough not to nag. */
export const TOAST_MS = 10_000;

/** At most this many at once — an older one drops rather than the corner filling up. */
const MAX_VISIBLE = 4;

export type ToastFn = (message: string, options?: ToastOptions) => void;

interface ToastItem {
  id: number;
  message: string;
  kind: ToastKind;
  duration: number;
  /** Set while the exit animation runs, so the row is not remounted mid-slide. */
  leaving?: boolean;
}

/**
 * A no-op outside the provider. Nothing in the app should reach this — main
 * mounts the provider around the whole router — but a page rendered on its own
 * (a test, a future embed) must not crash for want of somewhere to say "Saved".
 */
const ToastContext = createContext<ToastFn>(() => undefined);

/** `const toast = useToast(); toast('Saved.')` — kind defaults to ok. */
export function useToast(): ToastFn {
  return useContext(ToastContext);
}

let nextId = 1;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);

  const dismiss = useCallback((id: number) => {
    // Two steps: mark it leaving so it slides out, then drop it. A toast that
    // vanished instantly read as a glitch rather than a dismissal.
    setItems((list) => list.map((t) => (t.id === id ? { ...t, leaving: true } : t)));
    setTimeout(() => setItems((list) => list.filter((t) => t.id !== id)), 200);
  }, []);

  const toast = useCallback<ToastFn>((message, options) => {
    const kind = options?.kind || 'ok';
    const duration = options?.duration ?? TOAST_MS;
    setItems((list) => {
      // Pressing Save twice should restart one toast, not stack two identical
      // ones — so the same words in the same intent re-enter instead.
      const live = list.filter((t) => !(t.message === message && t.kind === kind && !t.leaving));
      const next = [...live, { id: nextId++, message, kind, duration }];
      return next.slice(-MAX_VISIBLE);
    });
  }, []);

  const value = useMemo(() => toast, [toast]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      {/*
        * aria-live rather than a role on each toast: the region exists from
        * first paint, so a screen reader announces what lands in it. Polite,
        * because a save confirmation must not interrupt what is being read.
        */}
      <div className="fm-toasts" role="status" aria-live="polite">
        {items.map((t) => <Toast key={t.id} item={t} onDismiss={() => dismiss(t.id)} />)}
      </div>
    </ToastContext.Provider>
  );
}

const ICON: Record<ToastKind, string> = { ok: '✓', error: '!', info: 'i' };

function Toast({ item, onDismiss }: { item: ToastItem; onDismiss: () => void }) {
  /** Hovering holds the toast open — reading a long line should not race a timer. */
  const [held, setHeld] = useState(false);
  const dismissRef = useRef(onDismiss);
  dismissRef.current = onDismiss;

  useEffect(() => {
    if (!item.duration || held || item.leaving) return;
    const timer = setTimeout(() => dismissRef.current(), item.duration);
    return () => clearTimeout(timer);
  }, [item.duration, item.leaving, held]);

  return (
    <div
      className={`fm-toast ${item.kind}${item.leaving ? ' leaving' : ''}`}
      onMouseEnter={() => setHeld(true)}
      onMouseLeave={() => setHeld(false)}
    >
      <span className="fm-toast-icon" aria-hidden="true">{ICON[item.kind]}</span>
      <span className="fm-toast-text">{item.message}</span>
      <button className="fm-toast-x" onClick={onDismiss} aria-label="Dismiss">✕</button>
      {!!item.duration && (
        <span
          className="fm-toast-life"
          style={{ animationDuration: `${item.duration}ms`, animationPlayState: held ? 'paused' : 'running' }}
        />
      )}
    </div>
  );
}
